import { createSlice } from "@reduxjs/toolkit";
import {
    fetchServices,
    createService,
    updateService,
    deleteService,
    fetchSubServices,
    createSubService,
    updateSubService,
    deleteSubService,
    fetchServiceItems,
    getServiceItemById,
    createServiceItem,
    updateServiceItem,
    deleteServiceItem,
} from "./ManageServiceThunk";
import { uniqueById } from "../../../utils/manageServiceValidation";

const initialState = {
    services: [],
    subServices: [],
    serviceItems: [],
    totalPages: 1,
    currentPage: 1,
    subServicesTotalPages: 1,
    subServicesCurrentPage: 1,
    subServicesTotalDocuments: 0,
    loading: false,

    fetchServicesSuccess: null,
    fetchServicesErr: null,
    createServiceSuccess: null,
    createServiceErr: null,
    updateServiceSuccess: null,
    updateServiceErr: null,
    deleteServiceSuccess: null,
    deleteServiceErr: null,

    fetchSubServicesSuccess: null,
    fetchSubServicesErr: null,
    createSubServiceSuccess: null,
    createSubServiceErr: null,
    updateSubServiceSuccess: null,
    updateSubServiceErr: null,
    deleteSubServiceSuccess: null,
    deleteSubServiceErr: null,

    fetchServiceItemsSuccess: null,
    fetchServiceItemsErr: null,
    getServiceItemByIdSuccess: null,
    getServiceItemByIdErr: null,
    createServiceItemSuccess: null,
    createServiceItemErr: null,
    updateServiceItemSuccess: null,
    updateServiceItemErr: null,
    deleteServiceItemSuccess: null,
    deleteServiceItemErr: null,

    serviceItemsTotalPages: 1,
    serviceItemsCurrentPage: 1,
    serviceItemsTotalDocuments: 0,
    serviceItemDetail: null,
};

const manageServiceSlice = createSlice({
    name: "manageService",
    initialState,
    reducers: {
        resetFetchServices(state) {
            state.fetchServicesSuccess = null;
            state.fetchServicesErr = null;
        },
        resetCreateService(state) {
            state.createServiceSuccess = null;
            state.createServiceErr = null;
        },
        resetUpdateService(state) {
            state.updateServiceSuccess = null;
            state.updateServiceErr = null;
        },
        resetDeleteService(state) {
            state.deleteServiceSuccess = null;
            state.deleteServiceErr = null;
        },
        resetFetchSubServices(state) {
            state.fetchSubServicesSuccess = null;
            state.fetchSubServicesErr = null;
        },
        resetCreateSubService(state) {
            state.createSubServiceSuccess = null;
            state.createSubServiceErr = null;
        },
        resetUpdateSubService(state) {
            state.updateSubServiceSuccess = null;
            state.updateSubServiceErr = null;
        },
        resetDeleteSubService(state) {
            state.deleteSubServiceSuccess = null;
            state.deleteSubServiceErr = null;
        },
        resetFetchServiceItems(state) {
            state.fetchServiceItemsSuccess = null;
            state.fetchServiceItemsErr = null;
        },
        resetGetServiceItemById(state) {
            state.getServiceItemByIdSuccess = null;
            state.getServiceItemByIdErr = null;
            state.serviceItemDetail = null;
        },
        resetCreateServiceItem(state) {
            state.createServiceItemSuccess = null;
            state.createServiceItemErr = null;
        },
        resetUpdateServiceItem(state) {
            state.updateServiceItemSuccess = null;
            state.updateServiceItemErr = null;
        },
        resetDeleteServiceItem(state) {
            state.deleteServiceItemSuccess = null;
            state.deleteServiceItemErr = null;
        },
        clearSubServices(state) {
            state.subServices = [];
        },
        clearServiceItems(state) {
            state.serviceItems = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH SERVICES
            .addCase(fetchServices.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchServices.fulfilled, (state, action) => {
                state.loading = false;
                state.fetchServicesSuccess = action.payload;
                state.services = uniqueById(action.payload?.data?.services || []);
                state.totalPages = action.payload?.data?.totalPages || 1;
                state.currentPage = action.payload?.data?.currentPage || 1;
            })
            .addCase(fetchServices.rejected, (state, action) => {
                state.loading = false;
                state.fetchServicesErr = action.payload;
            })

            // CREATE SERVICE
            .addCase(createService.pending, (state) => {
                state.loading = true;
            })
            .addCase(createService.fulfilled, (state, action) => {
                state.loading = false;
                state.createServiceSuccess = action.payload;
                const newService = action.payload?.data?.service;
                if (newService) {
                    state.services = uniqueById([newService, ...state.services]);
                }
            })
            .addCase(createService.rejected, (state, action) => {
                state.loading = false;
                state.createServiceErr = action.payload;
            })

            // UPDATE SERVICE
            .addCase(updateService.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateService.fulfilled, (state, action) => {
                state.loading = false;
                state.updateServiceSuccess = action.payload;
                const updated = action.payload?.data?.service;
                if (updated) {
                    state.services = uniqueById(state.services.map((s) =>
                        s._id === updated._id ? updated : s
                    ));
                }
            })
            .addCase(updateService.rejected, (state, action) => {
                state.loading = false;
                state.updateServiceErr = action.payload;
            })

            // DELETE SERVICE
            .addCase(deleteService.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteService.fulfilled, (state, action) => {
                state.loading = false;
                state.deleteServiceSuccess = action.payload;
                const updatedService = action.payload.data?.data?.service;
                if (updatedService) {
                    state.services = state.services.map((s) => s._id === updatedService._id ? updatedService : s);
                } else {
                    // Fallback to remove if not returned
                    state.services = state.services.filter((s) => s._id !== action.payload.id);
                }
            })
            .addCase(deleteService.rejected, (state, action) => {
                state.loading = false;
                state.deleteServiceErr = action.payload;
            })

            // FETCH SUB SERVICES
            .addCase(fetchSubServices.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSubServices.fulfilled, (state, action) => {
                state.loading = false;
                state.fetchSubServicesSuccess = action.payload;
                state.subServices = uniqueById(action.payload?.data?.subServices || []);
                state.subServicesTotalPages = action.payload?.data?.totalPages || 1;
                state.subServicesCurrentPage = action.payload?.data?.currentPage || 1;
                state.subServicesTotalDocuments =
                    action.payload?.data?.totalDocuments ||
                    action.payload?.data?.totalItems ||
                    action.payload?.data?.totalSubServices ||
                    state.subServices.length;
            })
            .addCase(fetchSubServices.rejected, (state, action) => {
                state.loading = false;
                state.fetchSubServicesErr = action.payload;
            })

            // CREATE SUB SERVICE
            .addCase(createSubService.pending, (state) => {
                state.loading = true;
            })
            .addCase(createSubService.fulfilled, (state, action) => {
                state.loading = false;
                state.createSubServiceSuccess = action.payload;
                const newSub = action.payload?.data?.subService;
                if (newSub) {
                    state.subServices = uniqueById([newSub, ...state.subServices]);
                }
            })
            .addCase(createSubService.rejected, (state, action) => {
                state.loading = false;
                state.createSubServiceErr = action.payload;
            })

            // UPDATE SUB SERVICE
            .addCase(updateSubService.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateSubService.fulfilled, (state, action) => {
                state.loading = false;
                state.updateSubServiceSuccess = action.payload;
                const updated = action.payload?.data?.subService;
                if (updated) {
                    state.subServices = uniqueById(state.subServices.map((s) =>
                        s._id === updated._id ? updated : s
                    ));
                }
            })
            .addCase(updateSubService.rejected, (state, action) => {
                state.loading = false;
                state.updateSubServiceErr = action.payload;
            })

            // DELETE SUB SERVICE
            .addCase(deleteSubService.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteSubService.fulfilled, (state, action) => {
                state.loading = false;
                state.deleteSubServiceSuccess = action.payload;
                const updatedSubService = action.payload.data?.data?.subService || action.payload.data?.data?.service;
                if (updatedSubService) {
                    state.subServices = state.subServices.map((s) => s._id === updatedSubService._id ? updatedSubService : s);
                } else {
                    state.subServices = state.subServices.filter((s) => s._id !== action.payload.id);
                }
            })
            .addCase(deleteSubService.rejected, (state, action) => {
                state.loading = false;
                state.deleteSubServiceErr = action.payload;
            })

            // FETCH SERVICE ITEMS
            .addCase(fetchServiceItems.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchServiceItems.fulfilled, (state, action) => {
                state.loading = false;
                state.fetchServiceItemsSuccess = action.payload;
                state.serviceItems = action.payload?.data?.serviceItems || [];
                state.serviceItemsTotalPages = action.payload?.data?.totalPages || 1;
                state.serviceItemsCurrentPage = action.payload?.data?.currentPage || 1;
                state.serviceItemsTotalDocuments =
                    action.payload?.data?.totalDocuments ||
                    action.payload?.data?.totalItems ||
                    action.payload?.data?.totalServiceItems ||
                    state.serviceItems.length;
            })
            .addCase(fetchServiceItems.rejected, (state, action) => {
                state.loading = false;
                state.fetchServiceItemsErr = action.payload;
            })

            // GET SERVICE ITEM BY ID
            .addCase(getServiceItemById.pending, (state) => {
                state.loading = true;
            })
            .addCase(getServiceItemById.fulfilled, (state, action) => {
                state.loading = false;
                state.getServiceItemByIdSuccess = action.payload;
                state.serviceItemDetail = action.payload?.data?.serviceItem || null;
            })
            .addCase(getServiceItemById.rejected, (state, action) => {
                state.loading = false;
                state.getServiceItemByIdErr = action.payload;
            })

            // CREATE SERVICE ITEM
            .addCase(createServiceItem.pending, (state) => {
                state.loading = true;
            })
            .addCase(createServiceItem.fulfilled, (state, action) => {
                state.loading = false;
                state.createServiceItemSuccess = action.payload;
                const newItem = action.payload?.data?.serviceItem;
                if (newItem) {
                    state.serviceItems = [newItem, ...state.serviceItems];
                }
            })
            .addCase(createServiceItem.rejected, (state, action) => {
                state.loading = false;
                state.createServiceItemErr = action.payload;
            })

            // UPDATE SERVICE ITEM
            .addCase(updateServiceItem.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateServiceItem.fulfilled, (state, action) => {
                state.loading = false;
                state.updateServiceItemSuccess = action.payload;
                const updated = action.payload?.data?.serviceItem;
                if (updated) {
                    state.serviceItems = state.serviceItems.map((i) =>
                        i._id === updated._id ? updated : i
                    );
                }
            })
            .addCase(updateServiceItem.rejected, (state, action) => {
                state.loading = false;
                state.updateServiceItemErr = action.payload;
            })

            // DELETE SERVICE ITEM
            .addCase(deleteServiceItem.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteServiceItem.fulfilled, (state, action) => {
                state.loading = false;
                state.deleteServiceItemSuccess = action.payload;
                const updatedItem = action.payload.data?.data?.serviceItem || action.payload.data?.data?.subService || action.payload.data?.data?.service;
                if (updatedItem) {
                    // Merge only the changed fields — the response's service/subService are
                    // unpopulated ID strings, so a full replace would drop the populated
                    // title objects the table needs to render Service Group / Sub-Service Group.
                    state.serviceItems = state.serviceItems.map((i) =>
                        i._id === updatedItem._id ? { ...i, isActive: updatedItem.isActive, updatedAt: updatedItem.updatedAt } : i
                    );
                } else {
                    state.serviceItems = state.serviceItems.filter((i) => i._id !== action.payload.id);
                }
            })
            .addCase(deleteServiceItem.rejected, (state, action) => {
                state.loading = false;
                state.deleteServiceItemErr = action.payload;
            });
    },
});

export const {
    resetFetchServices,
    resetCreateService,
    resetUpdateService,
    resetDeleteService,
    resetFetchSubServices,
    resetCreateSubService,
    resetUpdateSubService,
    resetDeleteSubService,
    resetFetchServiceItems,
    resetGetServiceItemById,
    resetCreateServiceItem,
    resetUpdateServiceItem,
    resetDeleteServiceItem,
    clearSubServices,
    clearServiceItems,
} = manageServiceSlice.actions;

export default manageServiceSlice.reducer;
